import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchPublicVersions, selectHighestVersion } from '../services/releaseApi';

const ReleaseOptionsContext = createContext(null);

export function ReleaseOptionsProvider({ selectedRelease, setSelectedRelease, children }) {
    const [availableVersions, setAvailableVersions]   = useState([]);
    const currentVersion = availableVersions.find(v => v.code === selectedRelease.version)
        || selectHighestVersion(availableVersions);
    const availableProducts = currentVersion?.products || [];
    const availableLanguages = currentVersion?.languages || [];

    // 1) Load public versions
    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const publicVersions = await fetchPublicVersions();

                if (cancelled) return;
                setAvailableVersions(publicVersions);
            } catch (err) {
                console.error(err);
                if (cancelled) return;
                setAvailableVersions([]);
            }
        })();

        return () => { cancelled = true; };
    }, []);


    // 2) Validate the selected release against public release metadata
    useEffect(() => {
        if (availableVersions.length === 0) return;

        // Validate the full release tuple and commit it atomically. This avoids
        // transient TOC requests for version-only or language-only selections.
        const ver = availableVersions.find(v => v.code === selectedRelease.version)
            || selectHighestVersion(availableVersions);
        const products = ver.products || [];
        const languages = ver.languages || [];
        const product = products.includes(selectedRelease.product)
            ? selectedRelease.product
            : products[0] || '';
        const language = languages.includes(selectedRelease.language)
            ? selectedRelease.language
            : languages[0] || '';

        if (selectedRelease.version !== ver.code ||
            selectedRelease.product !== product || selectedRelease.language !== language) {
            setSelectedRelease(selectedRelease.with({ version: ver.code, product, language }));
        }
    }, [
        selectedRelease,
        availableVersions,
        setSelectedRelease,
    ]);


    return (
        <ReleaseOptionsContext.Provider value={{
            availableVersions, availableProducts, availableLanguages,
            selectedRelease, setSelectedRelease,
        }}>
            {children}
        </ReleaseOptionsContext.Provider>
    );
}

export function useReleaseOptions() {
    const options = useContext(ReleaseOptionsContext);
    if (!options) throw new Error('Release selectors require ReleaseOptionsProvider');
    return options;
}
